import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";
import showErrorToast, { showSuccessToast } from "../toast/toast";

export default function ItemAssignmentModal({
  showModal,
  setShowModal,
  modifierGroup,
  modifierGroups,
  compID,
  onSuccess,
  isBulk = false,
}) {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filter items based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems(items);
    } else {
      const filtered = items.filter(item =>
        item.menuItem_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredItems(filtered);
    }
  }, [searchTerm, items]);

  // Fetch items when modal opens
  const fetchItems = useCallback(async () => {
    if (!compID || !showModal) return;

    try {
      setLoading(true);
      debugger;
      const [itemsResponse, relationsResponse] = await Promise.all([
        axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
          params: { CompID: compID },
        }),
        // Fetch existing relations to preselect items
        !isBulk && modifierGroup?.modifierGroupID
          ? axiosInstance.get(APIs.GET_ITEM_MODIFIER_GROUPS_RELATIONS, {
              params: { CompID: compID },
            }).catch(() => ({ data: { data: [] } })) // Handle error gracefully
          : isBulk && modifierGroups && modifierGroups.length > 0
          ? axiosInstance.get(APIs.GET_ITEM_MODIFIER_GROUPS_RELATIONS, {
              params: { CompID: compID },
            }).catch(() => ({ data: { data: [] } })) // Handle error gracefully
          : Promise.resolve({ data: { data: [] } })
      ]);

      const itemsData = itemsResponse.data.data || itemsResponse.data;
      if (Array.isArray(itemsData)) {
        setItems(itemsData);
        setFilteredItems(itemsData);

        // Preselect items that are already linked to the modifier group
        if (!isBulk && modifierGroup?.modifierGroupID) {
          const relationsData = relationsResponse.data.data || relationsResponse.data || [];
          const linkedItemIDs = Array.isArray(relationsData)
            ? relationsData
                .filter(relation => relation.modifierGroup_ID === modifierGroup.modifierGroupID )
                .map(relation => relation.menuItem_ID || relation.menuItem_ID)
            : [];

          if (linkedItemIDs.length > 0) {
            const preselectedItems = itemsData.filter(item => 
              linkedItemIDs.includes(item.menuItem_ID || item.MenuItem_ID)
            );
            setSelectedItems(preselectedItems);
          }
        } else if (isBulk && modifierGroups && modifierGroups.length > 0) {
          // For bulk mode, preselect items linked to any of the selected modifier groups
          const relationsData = relationsResponse.data.data || relationsResponse.data || [];
          const modifierGroupIDs = modifierGroups.map(g => g.modifierGroupID);
          const linkedItemIDs = Array.isArray(relationsData)
            ? relationsData
                .filter(relation => {
                  const groupID = relation.ModifierGroup_ID || relation.modifierGroupID;
                  return modifierGroupIDs.includes(groupID);
                })
                .map(relation => relation.MenuItem_ID || relation.menuItem_ID)
            : [];

          if (linkedItemIDs.length > 0) {
            const preselectedItems = itemsData.filter(item => 
              linkedItemIDs.includes(item.menuItem_ID || item.MenuItem_ID)
            );
            setSelectedItems(preselectedItems);
          }
        }
      } else {
        setItems([]);
        setFilteredItems([]);
      }
    } catch (err) {
      setItems([]);
      setFilteredItems([]);
      showErrorToast("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  }, [compID, showModal, modifierGroup, modifierGroups, isBulk]);

  useEffect(() => {
    if (showModal) {
      fetchItems();
      setSearchTerm("");
    }
  }, [showModal, fetchItems]);

  const handleItemSelection = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(selectedItem => selectedItem.menuItem_ID === item.menuItem_ID);
      if (isSelected) {
        return prev.filter(selectedItem => selectedItem.menuItem_ID !== item.menuItem_ID);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSubmit = async () => {
    if ((!modifierGroup && !isBulk) || (!modifierGroups && isBulk) || selectedItems.length === 0) {
      showErrorToast("Please select at least one item");
      return;
    }

    try {
      setSubmitting(true);

      if (isBulk) {
        // Handle bulk assignment to multiple add-on groups
        const modifierGroupIDs = modifierGroups.map(group => group.modifierGroupID);
        const itemIDs = selectedItems.map(item => item.menuItem_ID);

        const payload = {
          modifierGroupIDs: modifierGroupIDs,
          compID: compID,
          itemIDs: itemIDs
        };

        const response = await axiosInstance.post(APIs.POST_BULK_ASSIGN_ITEMS_TO_MODIFIER_GROUPS, payload);

        if (response.data.status) {
          showSuccessToast(`Successfully assigned ${selectedItems.length} item(s) to ${modifierGroups.length} add-on group(s)`);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          showErrorToast(response.data.message || "Failed to assign items to add-on group ");
        }
      } else {
        // Handle single add-on group assignment
        const payload = {
          modifierGroupID: modifierGroup.modifierGroupID,
          compID: compID,
          itemIDs: selectedItems.map(item => item.menuItem_ID)
        };

        const response = await axiosInstance.post(APIs.POST_ASSIGN_ITEMS_TO_MODIFIER_GROUP, payload);

        if (response.data.status) {
          showSuccessToast(`Successfully assigned "${modifierGroup.name}   to ${selectedItems.length} item(s) "`);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        } else {
          showErrorToast(response.data.message || "Failed to assign add-on group to items");
        }
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to assign add-on group(s) to items");
    } finally {
      setSubmitting(false);
    }
  };

  const RenderItemCard = ({ item }) => {
    const isSelected = selectedItems.some(selectedItem => selectedItem.menuItem_ID === item.menuItem_ID);

    return (
      <div
        className="item-card"
        style={{
          background: isSelected ? '#e3f2fd' : '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          border: isSelected ? '2px solid #007bff' : '2px solid #e9ecef',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: isSelected ? '0 4px 12px rgba(0, 123, 255, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
          marginBottom: '12px'
        }}
        onClick={() => handleItemSelection(item)}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = '#007bff';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = '#e9ecef';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <div className="d-flex justify-content-between align-items-center">

          {/* Item Image */}
          <div
            style={{
              width: '40px',
              height: '40px',
              background: isSelected ? '#007bff' : '#f8f9fa',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              border: '1px solid #e9ecef',
              overflow: 'hidden'
            }}
          >
            <LazyLoadImage
              src={item.menuItem_Image || "/assets/img/default-product.png"}
              alt={item.menuItem_Name}
              style={{
                height: "32px",
                width: "32px",
                objectFit: "contain",
              }}
            />
          </div>

          {/* Item Info */}
          <div className="flex-grow-1">
            <span
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: isSelected ? '#007bff' : '#495057',
                display: 'block',
                marginBottom: '2px'
              }}
            >
              {item.menuItem_Name}
            </span>
            {item.menuItem_SalePrice && (
              <small
                style={{
                  color: isSelected ? '#0056b3' : '#6c757d',
                  fontSize: '11px',
                  lineHeight: '1.3'
                }}
              >
                ${item.menuItem_SalePrice}
              </small>
            )}
          </div>
        </div>

        {/* Custom Checkbox */}
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: isSelected ? '2px solid #007bff' : '2px solid #e9ecef',
            background: isSelected ? '#007bff' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          {isSelected && (
            <i
              className="fa fa-check"
              style={{
                fontSize: '10px',
                color: 'white'
              }}
            ></i>
          )}
        </div>
      </div>
    );
  };

  if (!modifierGroup && !isBulk) return null;

  const getModalTitle = () => {
    if (isBulk && modifierGroups) {
      return `Assign Items to ${modifierGroups.length} Add-on Groups`;
    }
    return `Assign Items to "${modifierGroup?.name}"`;
  };

  const getModalDescription = () => {
    if (isBulk && modifierGroups) {
      return `Select the items to assign to the following add-on groups: ${modifierGroups.map(g => g.name).join(', ')}`;
    }
    return `Select the items to assign to this add-on group`;
  };

  return (
    <div>
      <CustomModal
        isOpen={showModal}
        title={getModalTitle()}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        isCentered={true}
        submitButtonText={submitting ? "Submitting..." : "Submit"}
        submitButtonDisabled={selectedItems.length === 0 || submitting}
      >
        <p style={{ color: '#6c757d', marginBottom: '20px' }}>
          {getModalDescription()}
        </p>

        {/* Search Input */}
        <div className="mb-4">
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#007bff';
                e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e9ecef';
                e.target.style.boxShadow = 'none';
              }}
            />
            <i
              className="fa fa-search"
              style={{
                position: 'absolute',
                right: '16px',
                color: '#6c757d',
                fontSize: '14px'
              }}
            ></i>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{
                  position: 'absolute',
                  right: '40px',
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.color = '#495057';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6c757d';
                }}
              >
                <i className="fa fa-times" style={{ fontSize: '12px' }}></i>
              </button>
            )}
          </div>
        </div>

        {/* Selection Info */}
        {selectedItems.length > 0 && (
          <div
            className="mb-3"
            style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '12px 16px',
              border: '1px solid #e9ecef'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                {selectedItems.length} item(s) selected
              </span>
              <button
                onClick={() => setSelectedItems([])}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#dc3545',
                  cursor: 'pointer',
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f8d7da';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Item Cards */}
        <div style={{ height: "400px", overflowY: "auto", paddingRight: '8px' }}>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2" style={{ color: '#6c757d' }}>Loading items...</p>
            </div>
          ) : filteredItems && filteredItems.length > 0 ? (
            <div>
              {filteredItems.map((item) => (
                <RenderItemCard key={item.menuItem_ID} item={item} />
              ))}
            </div>
          ) : (
            <div
              className="text-center py-5"
              style={{
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '2px dashed #e9ecef'
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  background: '#ffffff',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  border: '1px solid #e9ecef'
                }}
              >
                <i className="fa fa-box" style={{ fontSize: '24px', color: '#6c757d' }}></i>
              </div>
              <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '6px' }}>
                No items found
                {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
              </p>
              {searchTerm.length > 0 && (
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  Try adjusting your search terms
                </small>
              )}
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
}
